import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "@/lib/api";
import { Flag, SearchCheck, CheckCheck, XCircle, Inbox, CircleDot } from "lucide-react";

/** Phase 11.1 — Public correction report status tracker.
 * Lookup requires the full non-enumerable reference; reporter identity is never shown. */

const STATUS_CLS = {
  Received: "bg-amber-50 text-amber-900 border-amber-300",
  "Under Review": "bg-sky-50 text-sky-800 border-sky-200",
  Confirmed: "bg-violet-50 text-violet-800 border-violet-200",
  Corrected: "bg-emerald-50 text-emerald-800 border-emerald-200",
  Dismissed: "bg-slate-100 text-slate-600 border-slate-300",
};
const STATUS_ICON = {
  Received: Inbox, "Under Review": SearchCheck, Confirmed: CheckCheck,
  Corrected: CheckCheck, Dismissed: XCircle,
};

const fmt = (iso) => {
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

export default function CorrectionStatus() {
  const { reference: refParam } = useParams();
  const navigate = useNavigate();
  const [input, setInput] = useState(refParam || "");
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const lookup = useCallback((ref) => {
    if (!ref) return;
    setLoading(true); setError(null); setData(null);
    api.get(`/public/corrections/status/${encodeURIComponent(ref.trim())}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "Tracking reference not recognized."))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { if (refParam) lookup(refParam); }, [refParam, lookup]);

  const submit = (e) => {
    e.preventDefault();
    navigate(`/corrections/status/${encodeURIComponent(input.trim())}`);
    lookup(input);
  };

  return (
    <div className="wyv-container py-10 sm:py-14 max-w-2xl">
      <Badge variant="outline" className="bg-blue-50 text-blue-800 border-blue-200 mb-3">Corrections</Badge>
      <h1 className="font-heading font-bold text-3xl text-foreground" data-testid="correction-status-title">
        Check a correction report
      </h1>
      <p className="text-muted-foreground mt-2">
        Enter the tracking reference you received when you reported an error
        (it looks like <span className="font-mono text-xs">WYV-COR-2026-00042-…</span>).
        Every report follows: Received → Under Review → Confirmed → Corrected, or is Dismissed
        with an explanation.
      </p>

      <form onSubmit={submit} className="flex flex-wrap items-end gap-3 mt-5">
        <div className="flex-1 min-w-60">
          <Label className="text-xs text-muted-foreground">Tracking reference</Label>
          <Input className="mt-1 font-mono" value={input} placeholder="WYV-COR-2026-00042-XXXXXXXXXXXX"
                 onChange={(e) => setInput(e.target.value)} data-testid="correction-status-input" />
        </div>
        <Button type="submit" className="bg-primary hover:bg-primary/90" data-testid="correction-status-lookup-btn">
          Check status
        </Button>
      </form>

      {loading && <Skeleton className="h-56 mt-6" />}

      {error && !loading && (
        <Card className="mt-6" data-testid="correction-status-error">
          <CardContent className="p-6">
            <p className="text-sm text-foreground font-semibold">Not found</p>
            <p className="text-xs text-muted-foreground mt-1">{error} Check the full reference — the
              random ending is part of it and protects your report from being viewed by others.</p>
          </CardContent>
        </Card>
      )}

      {data && !loading && (
        <Card className="mt-6 border-t-4 border-t-primary" data-testid="correction-status-card">
          <CardContent className="p-6">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="font-mono text-sm text-foreground" data-testid="correction-status-reference">{data.reference}</p>
              <Badge variant="outline" className={`${STATUS_CLS[data.status] || ""} uppercase text-[10px]`}
                     data-testid="correction-status-badge">
                {data.status}
              </Badge>
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-muted-foreground">
              <span className="inline-flex items-center gap-1"><Flag className="h-3.5 w-3.5" /> {data.category}</span>
              <span>Submitted {fmt(data.submitted_at)}</span>
            </div>

            <div className="mt-5 space-y-0" data-testid="correction-status-timeline">
              {data.timeline.map((t, i) => {
                const Icon = STATUS_ICON[t.label] || CircleDot;
                return (
                  <div key={i} className="flex items-start gap-3 pb-4 relative">
                    {i < data.timeline.length - 1 && (
                      <span className="absolute left-[9px] top-6 bottom-0 w-px bg-slate-200" aria-hidden="true" />
                    )}
                    <span className="h-5 w-5 rounded-full bg-secondary border border-slate-300 flex items-center justify-center shrink-0 mt-0.5">
                      <Icon className="h-3 w-3 text-primary" />
                    </span>
                    <div>
                      <p className="text-sm font-semibold text-foreground">{t.label}</p>
                      <p className="text-[11px] text-muted-foreground">{fmt(t.at)}</p>
                    </div>
                  </div>
                );
              })}
            </div>

            {data.resolution && (
              <div className="rounded-lg border border-slate-200 bg-secondary p-4 mt-2" data-testid="correction-status-resolution">
                <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-1">Resolution</p>
                <p className="text-sm text-foreground whitespace-pre-wrap">{data.resolution}</p>
              </div>
            )}

            <p className="text-[10px] text-muted-foreground italic mt-4" data-testid="correction-status-note">{data.note}</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
